import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createOrUpdateStore, fetchMyStore } from '../store/storeSlice';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import { useConfirm } from '../../components/ui/ConfirmDialog';

// Default sections configuration
const DEFAULT_SECTIONS = [
  {
    id: 'hero',
    type: 'hero',
    name: 'Hero Section',
    description: 'Main banner with call-to-action',
    enabled: true,
    config: {
      title: 'Welcome to Our Store',
      subtitle: 'Discover amazing products and exceptional service',
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      showSecondaryButton: true,
      secondaryButtonText: 'Learn More',
      secondaryButtonLink: '/about'
    }
  },
  {
    id: 'featured-products',
    type: 'featured-products',
    name: 'Featured Products',
    description: 'Showcase featured products',
    enabled: true,
    config: {
      title: 'Featured Products',
      subtitle: 'Handpicked selections just for you',
      maxProducts: 6
    }
  },
  {
    id: 'features',
    type: 'features',
    name: 'Features Section',
    description: 'Highlight store benefits',
    enabled: true,
    config: {
      title: 'Why Shop With Us',
      subtitle: 'We\'re committed to providing you with the best shopping experience',
      features: [
        {
          icon: 'user',
          title: 'Personalized Service',
          description: 'Tailored recommendations and dedicated support for every customer'
        },
        {
          icon: 'check',
          title: 'Quality Guaranteed',
          description: 'Premium products carefully selected for superior quality and value'
        },
        {
          icon: 'zap',
          title: 'Fast Delivery',
          description: 'Quick and reliable shipping to get your orders to you faster'
        }
      ]
    }
  },
  {
    id: 'cta',
    type: 'cta',
    name: 'Call to Action',
    description: 'Encourage visitors to take action',
    enabled: true,
    config: {
      title: 'Ready to Start Shopping?',
      subtitle: 'Browse our collection and find exactly what you\'re looking for',
      buttonText: 'Explore Products',
      buttonLink: '/shop',
      showSecondaryButton: true,
      secondaryButtonText: 'Contact Us',
      secondaryButtonLink: '/contact'
    }
  }
];

const PageBuilder = () => {
  const dispatch = useDispatch();
  const { getAxios } = useSecureAuth();
  const store = useSelector(state => state.store?.current);
  const { confirm } = useConfirm();
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Initialize sections from store config or use defaults
  useEffect(() => {
    if (store?.pageConfig?.sections) {
      setSections(store.pageConfig.sections);
    } else {
      setSections(DEFAULT_SECTIONS);
    }
  }, [store]);

  const handleSectionToggle = (sectionId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, enabled: !section.enabled }
        : section
    ));
  };

  const handleSectionMove = (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sections.length - 1)
    ) {
      return;
    }

    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setSections(newSections);
  };

  const handleSectionConfig = (sectionId, newConfig) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, config: { ...section.config, ...newConfig } }
        : section
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const axios = getAxios(); // Get axios instance from secure auth
      const payload = {
        pageConfig: {
          sections: sections,
          lastModified: new Date().toISOString()
        }
      };

      await dispatch(createOrUpdateStore({ payload, axiosInstance: axios }));
      await dispatch(fetchMyStore(axios));

      setSaveMessage('Page layout saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Failed to save page layout:', error);
      setSaveMessage('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    const confirmed = await confirm('Are you sure you want to reset to default sections? This cannot be undone.', {
      title: 'Reset to Defaults',
      confirmText: 'Reset',
      cancelText: 'Cancel',
      type: 'warning'
    });
    if (!confirmed) return;

    setSections(DEFAULT_SECTIONS);
    setSaveMessage('Reset to defaults. Click Save to apply.');
  };

  return (
    <Shell>
      <div style={{
        padding: '2rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
      {/* Header */}
      <div style={{
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.875rem',
            fontWeight: '700',
            color: '#111827',
            margin: '0 0 0.5rem 0'
          }}>
            Page Builder
          </h1>
          <p style={{
            fontSize: '0.9375rem',
            color: '#6b7280',
            margin: 0
          }}>
            Customize your homepage by enabling, reordering, and configuring sections
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center'
        }}>
          <button
            onClick={handleReset}
            style={{
              padding: '0.625rem 1.25rem',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              fontSize: '0.9375rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.borderColor = '#d1d5db';
            }}
          >
            Reset to Defaults
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            style={{
              padding: '0.625rem 1.5rem',
              backgroundColor: isSaving ? '#9ca3af' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.9375rem',
              fontWeight: '500',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isSaving) e.target.style.backgroundColor = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              if (!isSaving) e.target.style.backgroundColor = '#2563eb';
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div style={{
          padding: '1rem',
          backgroundColor: saveMessage.includes('Failed') ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${saveMessage.includes('Failed') ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          color: saveMessage.includes('Failed') ? '#991b1b' : '#166534'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedSection ? '2fr 1fr' : '1fr',
        gap: '1.5rem'
      }}>
        {/* Sections List */}
        <div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            {sections.map((section, index) => (
              <div
                key={section.id}
                style={{
                  padding: '1.25rem',
                  borderBottom: index < sections.length - 1 ? '1px solid #e5e7eb' : 'none',
                  backgroundColor: section.enabled ? 'white' : '#f9fafb',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem'
                }}>
                  {/* Section Info */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: section.enabled ? '#111827' : '#6b7280',
                      margin: '0 0 0.25rem 0'
                    }}>
                      {section.name}
                    </h3>
                    <p style={{
                      fontSize: '0.875rem',
                      color: '#6b7280',
                      margin: 0
                    }}>
                      {section.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    {/* Reorder Buttons */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem'
                    }}>
                      <button
                        onClick={() => handleSectionMove(section.id, 'up')}
                        disabled={index === 0}
                        style={{
                          padding: '0.25rem',
                          backgroundColor: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.25rem',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          opacity: index === 0 ? 0.4 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                          <polyline points="18 15 12 9 6 15"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => handleSectionMove(section.id, 'down')}
                        disabled={index === sections.length - 1}
                        style={{
                          padding: '0.25rem',
                          backgroundColor: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.25rem',
                          cursor: index === sections.length - 1 ? 'not-allowed' : 'pointer',
                          opacity: index === sections.length - 1 ? 0.4 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    </div>

                    {/* Configure Button */}
                    <button
                      onClick={() => setSelectedSection(selectedSection?.id === section.id ? null : section)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: selectedSection?.id === section.id ? '#2563eb' : 'white',
                        color: selectedSection?.id === section.id ? 'white' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {selectedSection?.id === section.id ? 'Close' : 'Configure'}
                    </button>

                    {/* Toggle Switch */}
                    <label style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '44px',
                      height: '24px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={section.enabled}
                        onChange={() => handleSectionToggle(section.id)}
                        style={{ display: 'none' }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: section.enabled ? '#2563eb' : '#d1d5db',
                        borderRadius: '24px',
                        transition: 'background-color 0.2s ease'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '',
                          height: '18px',
                          width: '18px',
                          left: section.enabled ? '23px' : '3px',
                          bottom: '3px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: 'left 0.2s ease'
                        }} />
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Configuration Panel */}
        {selectedSection && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            padding: '1.5rem',
            position: 'sticky',
            top: '2rem',
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 8rem)',
            overflowY: 'auto'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              color: '#111827',
              margin: '0 0 1rem 0'
            }}>
              Configure {selectedSection.name}
            </h3>

            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              margin: '0 0 1.5rem 0'
            }}>
              {selectedSection.description}
            </p>

            {/* Configuration fields based on section type */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {selectedSection.config.title !== undefined && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Title
                  </label>
                  <input
                    type="text"
                    value={selectedSection.config.title}
                    onChange={(e) => handleSectionConfig(selectedSection.id, { title: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              )}

              {selectedSection.config.subtitle !== undefined && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Subtitle
                  </label>
                  <textarea
                    value={selectedSection.config.subtitle}
                    onChange={(e) => handleSectionConfig(selectedSection.id, { subtitle: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              )}

              {selectedSection.config.buttonText !== undefined && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={selectedSection.config.buttonText}
                    onChange={(e) => handleSectionConfig(selectedSection.id, { buttonText: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              )}

              {selectedSection.config.buttonLink !== undefined && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Button Link
                  </label>
                  <input
                    type="text"
                    value={selectedSection.config.buttonLink}
                    onChange={(e) => handleSectionConfig(selectedSection.id, { buttonLink: e.target.value })}
                    placeholder="/shop"
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              )}

              {selectedSection.config.maxProducts !== undefined && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Max Products to Show
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={selectedSection.config.maxProducts}
                    onChange={(e) => handleSectionConfig(selectedSection.id, { maxProducts: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              )}

              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                color: '#166534'
              }}>
                Don't forget to click "Save Changes" to apply your updates!
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </Shell>
  );
};

export default PageBuilder;
